/**
 * PL/SQL Monarch tokenizer — extends standard SQL highlighting with
 * Oracle PL/SQL block keywords, types, and package-structure tokens.
 */
export const plsqlMonarchTokenizer = {
  defaultToken: '',
  tokenPostfix: '.plsql',
  ignoreCase: true,

  brackets: [
    { open: '[', close: ']', token: 'delimiter.square' },
    { open: '(', close: ')', token: 'delimiter.parenthesis' },
  ],

  // Standard SQL keywords
  keywords: [
    'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE',
    'CREATE', 'ALTER', 'DROP', 'TABLE', 'VIEW', 'INDEX',
    'CONSTRAINT', 'PRIMARY', 'FOREIGN', 'KEY', 'JOIN', 'LEFT',
    'RIGHT', 'INNER', 'OUTER', 'FULL', 'CROSS', 'GROUP', 'ORDER',
    'BY', 'HAVING', 'AS', 'ON', 'AND', 'OR', 'NOT', 'NULL', 'IS',
    'IN', 'BETWEEN', 'LIKE', 'DISTINCT', 'COUNT', 'SUM', 'AVG',
    'MIN', 'MAX', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'WITH',
    'UNION', 'ALL', 'INTO', 'VALUES', 'SET', 'EXISTS', 'GRANT',
    'REVOKE', 'COMMIT', 'ROLLBACK', 'SAVEPOINT', 'TRUNCATE',
    'MERGE', 'USING', 'MATCHED',
  ],

  // PL/SQL block & control-flow keywords — highlighted distinctly
  plsqlKeywords: [
    'DECLARE', 'BEGIN', 'EXCEPTION', 'RAISE', 'RAISE_APPLICATION_ERROR',
    'IF', 'ELSIF', 'LOOP', 'WHILE', 'FOR', 'EXIT', 'CONTINUE',
    'RETURN', 'GOTO', 'EXECUTE', 'IMMEDIATE',
    'OPEN', 'FETCH', 'CLOSE', 'CURSOR',
    'BULK', 'COLLECT', 'FORALL', 'LIMIT', 'SAVE', 'EXCEPTIONS',
    'PIPE', 'ROW', 'PIPELINED',
    'PRAGMA', 'AUTONOMOUS_TRANSACTION', 'EXCEPTION_INIT',
    'RESTRICT_REFERENCES', 'SERIALLY_REUSABLE',
    'PACKAGE', 'BODY', 'PROCEDURE', 'FUNCTION', 'TRIGGER',
    'REPLACE', 'DETERMINISTIC', 'RESULT_CACHE',
    'TYPE', 'SUBTYPE', 'RECORD', 'VARRAY', 'NESTED',
    'AUTHID', 'CURRENT_USER', 'DEFINER',
    'NOCOPY', 'DEFAULT', 'CONSTANT',
    'OUT',
  ],

  // Oracle types
  typeKeywords: [
    'NUMBER', 'INTEGER', 'PLS_INTEGER', 'BINARY_INTEGER',
    'BINARY_FLOAT', 'BINARY_DOUBLE', 'SIMPLE_INTEGER',
    'VARCHAR2', 'NVARCHAR2', 'CHAR', 'NCHAR', 'LONG',
    'DATE', 'TIMESTAMP', 'INTERVAL',
    'BOOLEAN',
    'CLOB', 'NCLOB', 'BLOB', 'BFILE', 'RAW', 'LONG RAW',
    'ROWID', 'UROWID',
    'SYS_REFCURSOR', 'XMLTYPE', 'JSON',
  ],

  // Built-in Oracle functions commonly used in PL/SQL
  builtinFunctions: [
    'NVL', 'NVL2', 'COALESCE', 'DECODE', 'GREATEST', 'LEAST',
    'TO_CHAR', 'TO_NUMBER', 'TO_DATE', 'TO_TIMESTAMP', 'TO_CLOB',
    'SUBSTR', 'INSTR', 'LENGTH', 'REPLACE', 'TRIM', 'LTRIM', 'RTRIM',
    'UPPER', 'LOWER', 'INITCAP', 'LPAD', 'RPAD', 'TRANSLATE',
    'ROUND', 'TRUNC', 'MOD', 'ABS', 'CEIL', 'FLOOR', 'POWER', 'SQRT',
    'SYSDATE', 'SYSTIMESTAMP', 'CURRENT_DATE', 'CURRENT_TIMESTAMP',
    'ADD_MONTHS', 'MONTHS_BETWEEN', 'LAST_DAY', 'NEXT_DAY', 'EXTRACT',
    'CAST', 'CONVERT', 'HEXTORAW', 'RAWTOHEX',
    'DBMS_OUTPUT', 'DBMS_DEBUG', 'DBMS_SQL', 'DBMS_LOB',
    'UTL_FILE', 'UTL_HTTP', 'UTL_RAW',
    'RAISE_APPLICATION_ERROR',
    'SQL%ROWCOUNT', 'SQL%FOUND', 'SQL%NOTFOUND', 'SQL%ISOPEN',
  ],

  operators: [
    '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=',
    '<>', '||', '+', '-', '*', '/', '%', ':=', '=>', '..',
  ],

   
  symbols: /[=><!~?:&|+\-*/^%]+/,
   
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

  tokenizer: {
    root: [
      // %TYPE, %ROWTYPE anchored attributes
      [/%(?:TYPE|ROWTYPE)\b/i, 'type'],

      // Identifiers and keywords
      [/[a-zA-Z_$][\w$]*/, {
        cases: {
          '@plsqlKeywords': 'keyword.plsql',
          '@typeKeywords': 'type',
          '@builtinFunctions': 'predefined',
          '@keywords': 'keyword',
          '@default': 'identifier',
        },
      }],

      // Numbers (integer and decimal)
      [/\d+(\.\d+)?/, 'number'],

      // Strings (single-quoted)
      [/'/, { token: 'string', bracket: '@open', next: '@string' }],

      // Quoted identifiers (double-quoted)
      [/"/, { token: 'string.double', bracket: '@open', next: '@stringDouble' }],

      // Whitespace & comments
      { include: '@whitespace' },

      // Delimiters and operators.
      // Only characters declared in the `brackets` array above may be emitted
      // as `@brackets` — otherwise Monaco throws "no bracket defined as: X".
      // `<` / `>` / `{` / `}` are not PL/SQL brackets: `<` and `>` are
      // comparison operators (handled by `@symbols` below) and `{` / `}` do
      // not occur in PL/SQL syntax at all.
      [/[()[\]]/, '@brackets'],
      [/@symbols/, {
        cases: {
          '@operators': 'operator',
          '@default': '',
        },
      }],

      // Semicolons, commas, dots
      [/[;,.]/, 'delimiter'],

      // Bind variables :name and &substitution
      [/[:&]\w+/, 'variable'],
    ],

    string: [
      [/[^']+/, 'string'],
      [/''/, 'string'],       // escaped single-quote
      [/'/, { token: 'string', bracket: '@close', next: '@pop' }],
    ],

    stringDouble: [
      [/[^"]+/, 'string.double'],
      [/""/, 'string.double'], // escaped double-quote
      [/"/, { token: 'string.double', bracket: '@close', next: '@pop' }],
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white'],
      [/--.*$/, 'comment'],
      [/\/\*/, { token: 'comment.quote', next: '@comment' }],
    ],

    comment: [
      [/[^*/]+/, 'comment'],
      [/\*\//, { token: 'comment.quote', next: '@pop' }],
      [/./, 'comment'],
    ],
  },
};
